import React, { useState, useEffect, useRef } from 'react';
import hbjs from 'harfbuzzjs/hbjs.js';
import './ArabicTextPreview.css';

// Local font URLs (webpack will hash & emit them)
import aldhabiUrl            from '../fonts/Aldhabi-Regular.ttf';
import aishaUrl              from '../fonts/aisha-arabic-regular.ttf';
import amiriRegularUrl       from '../fonts/Amiri-Regular.ttf';
import amiriBoldUrl          from '../fonts/Amiri-Bold.ttf';
import bixieUrl              from '../fonts/BIXIE-Regular.ttf';
import dgBeboRegularUrl      from '../fonts/DG-Bebo-Regular.ttf';
import dgBeboBoldUrl         from '../fonts/DG-Bebo-Bold.ttf';
import dgForshaRegularUrl    from '../fonts/DG-Forsha-Regular.ttf';
import dgForshaScribbleUrl   from '../fonts/DG-Forsha-Scribble.ttf';
import graphicSchoolUrl      from '../fonts/GraphicSchool-Regular.ttf';
import blakaUrl              from '../fonts/Blaka-Regular.ttf';
import arefRuqaaInkUrl       from '../fonts/ArefRuqaaInk-Regular.ttf';
import arefRuqaaInkBoldUrl   from '../fonts/ArefRuqaaInk-Bold.ttf';
import ibmPlexArabicUrl      from '../fonts/IBMPlexSansArabic-Regular.ttf';
import ibmPlexArabicBoldUrl  from '../fonts/IBMPlexSansArabic-Bold.ttf';
import lalezarUrl            from '../fonts/Lalezar-Regular.ttf';
import reemKufiInkUrl        from '../fonts/ReemKufiInk-Regular.ttf';
import khalayUrl             from '../fonts/Khalay-Regular.ttf';

// HarfBuzz Emscripten module is loaded lazily via a runtime <script> tag.
// We can't `import` it normally because it contains a `require('fs')` (in a
// dead Node.js branch) that webpack tries to resolve at build time and fails.
// Loading the file at runtime sidesteps webpack entirely.
const HB_BASE = `${process.env.PUBLIC_URL || ''}/harfbuzz`;
let hbPromise = null;
const loadHarfbuzz = () => {
  if (hbPromise) return hbPromise;
  hbPromise = (async () => {
    if (typeof window.createHarfBuzz !== 'function') {
      await new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = `${HB_BASE}/hb.js`;
        s.async = true;
        s.onload  = () => resolve();
        s.onerror = () => reject(new Error('Failed to load harfbuzz/hb.js'));
        document.head.appendChild(s);
      });
    }
    const instance = await window.createHarfBuzz({
      locateFile: (p) => `${HB_BASE}/${p}`,
    });
    return hbjs(instance);
  })();
  return hbPromise;
};

// id -> { label, family, weight, src: 'local'|'google', url? }
const FONT_CONFIG = {
  'aldhabi':              { label: 'Aldhabi (Website Default)',         family: 'Aldhabi',              weight: 400, src: 'local',  url: aldhabiUrl            },
  'aisha':                { label: 'Aisha Arabic',                      family: 'Aisha Arabic',         weight: 400, src: 'local',  url: aishaUrl              },
  'amiri':                { label: 'Amiri Regular',                     family: 'Amiri',                weight: 400, src: 'local',  url: amiriRegularUrl       },
  'amiri-bold':           { label: 'Amiri Bold',                        family: 'Amiri',                weight: 700, src: 'local',  url: amiriBoldUrl          },
  'bixie':                { label: 'BIXIE',                             family: 'BIXIE',                weight: 400, src: 'local',  url: bixieUrl              },
  'dg-bebo':              { label: 'DG Bebo Regular',                   family: 'DG Bebo',              weight: 400, src: 'local',  url: dgBeboRegularUrl      },
  'dg-bebo-bold':         { label: 'DG Bebo Bold',                      family: 'DG Bebo',              weight: 700, src: 'local',  url: dgBeboBoldUrl         },
  'dg-forsha':            { label: 'DG Forsha Regular',                 family: 'DG Forsha',            weight: 400, src: 'local',  url: dgForshaRegularUrl    },
  'dg-forsha-scribble':   { label: 'DG Forsha Scribble',                family: 'DG Forsha Scribble',   weight: 400, src: 'local',  url: dgForshaScribbleUrl   },
  'graphic-school':       { label: 'Graphic School',                    family: 'Graphic School',       weight: 400, src: 'local',  url: graphicSchoolUrl      },
  'blaka':                { label: 'Blaka',                             family: 'Blaka',                weight: 400, src: 'local',  url: blakaUrl              },
  'aref-ruqaa-ink':       { label: 'Aref Ruqaa Ink',                    family: 'Aref Ruqaa Ink',       weight: 400, src: 'local',  url: arefRuqaaInkUrl       },
  'aref-ruqaa-ink-bold':  { label: 'Aref Ruqaa Ink Bold',               family: 'Aref Ruqaa Ink',       weight: 700, src: 'local',  url: arefRuqaaInkBoldUrl   },
  'ibm-plex':             { label: 'IBM Plex Sans Arabic',              family: 'IBM Plex Sans Arabic', weight: 400, src: 'local',  url: ibmPlexArabicUrl      },
  'ibm-plex-bold':        { label: 'IBM Plex Sans Arabic Bold',         family: 'IBM Plex Sans Arabic', weight: 700, src: 'local',  url: ibmPlexArabicBoldUrl  },
  'lalezar':              { label: 'Lalezar',                           family: 'Lalezar',              weight: 400, src: 'local',  url: lalezarUrl            },
  'reem-kufi-ink':        { label: 'Reem Kufi Ink',                     family: 'Reem Kufi Ink',        weight: 400, src: 'local',  url: reemKufiInkUrl        },
  'khalay':               { label: 'Khalay Typeface',                   family: 'Khalay',               weight: 400, src: 'local',  url: khalayUrl             },
};

const FONTS = Object.entries(FONT_CONFIG).map(([id, cfg]) => ({ id, label: cfg.label }));

const VW_SIZES  = [3, 5, 7, 10, 13, 16, 20, 25, 30, 40, 50];
const PX_SIZES  = [24, 36, 48, 64, 80, 96, 120, 150, 180, 220, 280, 360, 480];

export default function ArabicTextPreview() {
  const [text, setText]       = useState('');
  const [fontId, setFontId]   = useState('aldhabi');
  const [unit, setUnit]       = useState('vw');         // 'vw' | 'px'
  const [fontSize, setFontSize] = useState(10);         // value without unit
  const [color, setColor]     = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [fitWidth, setFitWidth] = useState(false);

  const containerRef = useRef(null);
  const textRef      = useRef(null);

  const activeFont = FONTS.find(f => f.id === fontId) || FONTS[0];
  const sizes      = unit === 'vw' ? VW_SIZES : PX_SIZES;
  const fontSizeStr = `${fontSize}${unit}`;

  // When unit changes reset to a sensible default
  const handleUnitChange = (u) => {
    setUnit(u);
    setFontSize(u === 'vw' ? 10 : 120);
    setFitWidth(false);
  };

  // Fit-to-width: scale the text span so it exactly fills the container
  useEffect(() => {
    if (!fitWidth || !containerRef.current || !textRef.current || !text) return;
    const span = textRef.current;
    // Reset transform first so we measure natural width
    span.style.transform = 'none';
    const containerW = containerRef.current.clientWidth - 96; // minus padding
    const naturalW   = span.scrollWidth;
    if (naturalW > 0) {
      const scale = containerW / naturalW;
      span.style.transform      = `scaleX(${scale})`;
      span.style.transformOrigin = 'center center';
      span.style.display         = 'block';
    }
  }, [text, fontId, fontSize, unit, fitWidth]);

  // Clear transform when fitWidth is off
  useEffect(() => {
    if (!fitWidth && textRef.current) {
      textRef.current.style.transform = 'none';
      textRef.current.style.display   = '';
    }
  }, [fitWidth]);

  // ─── Download helpers ─────────────────────────────────────────
  const [downloading, setDownloading] = useState(null); // 'png' | 'svg' | null

  const cfg = FONT_CONFIG[fontId];

  // Convert current size to actual px (for canvas/svg pixel math)
  const sizeInPx = unit === 'px'
    ? fontSize
    : Math.round((window.innerWidth * fontSize) / 100);

  // Build measurements & lines (shared by PNG/SVG)
  const buildLayout = () => {
    const lines  = (text || '').split('\n');
    const padX   = Math.max(24, Math.round(sizeInPx * 0.4));
    const padY   = Math.max(20, Math.round(sizeInPx * 0.25));
    const lineH  = Math.round(sizeInPx * 1.4);

    // Use canvas to measure each line
    const meas = document.createElement('canvas').getContext('2d');
    meas.font = `${cfg.weight} ${sizeInPx}px "${cfg.family}", serif`;
    const widths = lines.map(l => meas.measureText(l).width);
    const maxW   = Math.max(1, ...widths);

    const W = Math.ceil(maxW + padX * 2);
    const H = Math.ceil(lineH * lines.length + padY * 2);
    return { lines, widths, padX, padY, lineH, W, H };
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const safeName = () => {
    const fontPrefix = (cfg.label || 'Font').replace(/[^a-zA-Z0-9\s-]/g, '').trim();
    return `${fontPrefix} - `;
  };

  const handleDownloadPng = async () => {
    if (!text.trim()) return;
    setDownloading('png');
    try {
      // Make sure font is fully loaded
      await document.fonts.load(`${cfg.weight} ${sizeInPx}px "${cfg.family}"`);
      await document.fonts.ready;

      const { lines, lineH, W, H } = buildLayout();
      const dpr = 3; // high-DPI
      const canvas = document.createElement('canvas');
      canvas.width  = W * dpr;
      canvas.height = H * dpr;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);

      // Always start with a fully transparent canvas
      ctx.clearRect(0, 0, W, H);

      // Text – centered horizontally + vertically (background stays transparent)
      ctx.fillStyle = color;
      ctx.font = `${cfg.weight} ${sizeInPx}px "${cfg.family}", serif`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.direction = 'rtl';

      const totalTextH = lineH * lines.length;
      const startY = (H - totalTextH) / 2 + lineH / 2; // center vertically
      lines.forEach((line, i) => {
        ctx.fillText(line, W / 2, startY + i * lineH);
      });

      canvas.toBlob(
        (blob) => blob && downloadBlob(blob, `${safeName()}.png`),
        'image/png',
        1.0,
      );
    } catch (err) {
      console.error('PNG export failed:', err);
      alert('Failed to export PNG. See console for details.');
    } finally {
      setDownloading(null);
    }
  };

  // High-DPI canvas render – used as a fallback for Google Fonts in the SVG
  // exporter (browsers can't fetch the TTF for Google fonts via fetch because
  // we can't override the User-Agent).
  const renderTextCanvas = async (dpr = 4) => {
    await document.fonts.load(`${cfg.weight} ${sizeInPx}px "${cfg.family}"`);
    await document.fonts.ready;

    const { lines, lineH, W, H } = buildLayout();
    const canvas = document.createElement('canvas');
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    ctx.fillStyle = color;
    ctx.font = `${cfg.weight} ${sizeInPx}px "${cfg.family}", serif`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.direction = 'rtl';

    const totalTextH = lineH * lines.length;
    const startY = (H - totalTextH) / 2 + lineH / 2;
    lines.forEach((line, i) => ctx.fillText(line, W / 2, startY + i * lineH));
    return { canvas, W, H };
  };

  // Build a true vector SVG using HarfBuzz shaping + glyph paths.
  // Each Arabic letter becomes an editable <path> element with anchor points
  // when opened in Illustrator/Inkscape/Figma.
  const buildVectorSvg = async () => {
    const hb = await loadHarfbuzz();
    const fontRes = await fetch(cfg.url);
    const fontBuffer = await fontRes.arrayBuffer();

    const blob   = hb.createBlob(fontBuffer);
    const face   = hb.createFace(blob, 0);
    const font   = hb.createFont(face);
    const upem   = face.upem;
    const scale  = sizeInPx / upem;

    // Horizontal-extent metrics (in font units)
    const extents = font.hExtents();
    const ascender  = extents.ascender;
    const descender = extents.descender;          // negative
    const lineGap   = extents.lineGap || 0;
    const lineH     = (ascender - descender + lineGap) * scale;

    // Shape each line, capture glyphs + total advance
    const lines = text.split('\n');
    const shapedLines = lines.map(line => {
      const buf = hb.createBuffer();
      buf.addText(line);
      buf.guessSegmentProperties();                // auto-detects Arabic + RTL
      hb.shape(font, buf);
      const glyphs = buf.json();
      const advance = glyphs.reduce((s, g) => s + g.ax, 0) * scale;
      buf.destroy();
      return { glyphs, advance };
    });

    const maxAdvance = Math.max(1, ...shapedLines.map(l => l.advance));
    const padX = Math.max(24, Math.round(sizeInPx * 0.3));
    const padY = Math.max(20, Math.round(sizeInPx * 0.2));
    const W    = Math.ceil(maxAdvance + padX * 2);
    const H    = Math.ceil(lineH * lines.length + padY * 2);

    // Cache repeated glyph paths
    const glyphPathCache = new Map();
    const getGlyphPath = (gid) => {
      if (!glyphPathCache.has(gid)) glyphPathCache.set(gid, font.glyphToPath(gid));
      return glyphPathCache.get(gid);
    };

    // Each glyph: translate to pen position, scale, flip Y (HB is Y-up)
    const groupEls = [];
    shapedLines.forEach(({ glyphs, advance }, lineIdx) => {
      let cursorX = (W - advance) / 2;                                    // center horizontally
      let cursorY = padY + lineIdx * lineH + ascender * scale;            // baseline
      for (const g of glyphs) {
        const path = getGlyphPath(g.g);
        if (path && path.length > 0) {
          const x = cursorX + (g.dx || 0) * scale;
          const y = cursorY - (g.dy || 0) * scale;
          groupEls.push(
            `<g transform="translate(${x.toFixed(2)} ${y.toFixed(2)}) scale(${scale.toFixed(6)} ${(-scale).toFixed(6)})"><path d="${path}"/></g>`
          );
        }
        cursorX += (g.ax || 0) * scale;
        cursorY -= (g.ay || 0) * scale;
      }
    });

    font.destroy();
    face.destroy();
    blob.destroy();

    return `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <g fill="${color}" stroke="none">
    ${groupEls.join('\n    ')}
  </g>
</svg>`;
  };

  // Fallback for Google Fonts: high-DPI canvas embedded as base64 image.
  // Not editable as paths but produces a portable, crisp SVG.
  const buildRasterSvg = async () => {
    const { canvas, W, H } = await renderTextCanvas(4);
    const dataUri = canvas.toDataURL('image/png');
    return `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <image x="0" y="0" width="${W}" height="${H}" preserveAspectRatio="xMidYMid meet" xlink:href="${dataUri}"/>
</svg>`;
  };

  const handleDownloadSvg = async () => {
    if (!text.trim()) return;
    setDownloading('svg');
    try {
      // Local fonts get true editable vector paths via HarfBuzz.
      // Google fonts can't be fetched as TTF from the browser (CORS / UA),
      // so they use a high-DPI raster fallback that still produces a valid SVG.
      const svg = (cfg.src === 'local' && cfg.url)
        ? await buildVectorSvg()
        : await buildRasterSvg();

      const out = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
      downloadBlob(out, `${safeName()}.svg`);
    } catch (err) {
      console.error('SVG export failed:', err);
      alert(`Failed to export SVG: ${err.message || err}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="atp-root">
      <div className="atp-wrap">

        <h1 className="atp-title">Arabic Text Preview</h1>
        <p className="atp-sub">Type Arabic text below to preview it in the website fonts.</p>

        {/* Controls */}
        <div className="atp-controls">

          <div className="atp-control-group">
            <label className="atp-label">Font</label>
            <select className="atp-select" value={fontId} onChange={e => setFontId(e.target.value)}>
              {FONTS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>

          {/* Unit toggle + size */}
          <div className="atp-control-group">
            <label className="atp-label">Size</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <div className="atp-unit-toggle">
                <button
                  className={`atp-unit-btn${unit === 'vw' ? ' active' : ''}`}
                  onClick={() => handleUnitChange('vw')}
                >vw</button>
                <button
                  className={`atp-unit-btn${unit === 'px' ? ' active' : ''}`}
                  onClick={() => handleUnitChange('px')}
                >px</button>
              </div>
              <select
                className="atp-select"
                style={{ minWidth: 100 }}
                value={fontSize}
                onChange={e => setFontSize(Number(e.target.value))}
              >
                {sizes.map(s => <option key={s} value={s}>{s}{unit}</option>)}
              </select>
            </div>
          </div>

          <div className="atp-control-group">
            <label className="atp-label">Text Color</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="color" value={color} onChange={e => setColor(e.target.value)}
                style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 6, padding: 2 }} />
              <span className="atp-info" style={{ margin: 0 }}>{color}</span>
            </div>
          </div>

          <div className="atp-control-group">
            <label className="atp-label">Preview Background</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)}
                style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 6, padding: 2 }} />
              <span className="atp-info" style={{ margin: 0, fontSize: 11 }}>preview only — exports are transparent</span>
            </div>
          </div>

          <div className="atp-control-group">
            <label className="atp-label">Presets</label>
            <div className="atp-presets">
              <button className="atp-preset-btn" onClick={() => { setColor('#000000'); setBgColor('#ffffff'); }}>B / W</button>
              <button className="atp-preset-btn" onClick={() => { setColor('#ffffff'); setBgColor('#000000'); }}>W / B</button>
              <button className="atp-preset-btn" onClick={() => { setColor('#d4af37'); setBgColor('#1a1a1a'); }}>Gold</button>
            </div>
          </div>

          {/* Fit to width toggle */}
          <div className="atp-control-group" style={{ justifyContent: 'flex-end' }}>
            <label className="atp-label">Fit Width</label>
            <button
              className={`atp-preset-btn${fitWidth ? ' atp-fit-active' : ''}`}
              onClick={() => setFitWidth(v => !v)}
              title="Stretch text to fill 100% width"
            >
              {fitWidth ? '↔ ON' : '↔ OFF'}
            </button>
          </div>

        </div>

        {/* Input */}
        <textarea
          className={`atp-textarea atp-font-${fontId}`}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="اكتب النص العربي هنا..."
          dir="rtl"
          rows={3}
          style={{ fontSize: unit === 'vw' ? '3vw' : '28px' }}
        />

        {/* Preview label row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
          <span className="atp-label">Preview — {activeFont.label}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="atp-download-btn atp-download-png"
              onClick={handleDownloadPng}
              disabled={!text.trim() || downloading !== null}
              title="Download high-resolution PNG (3x DPI)"
            >
              {downloading === 'png' ? '⏳ Exporting…' : '⬇ PNG (high-res)'}
            </button>
            <button
              className="atp-download-btn atp-download-svg"
              onClick={handleDownloadSvg}
              disabled={!text.trim() || downloading !== null}
              title="Download SVG with embedded font (scalable, portable)"
            >
              {downloading === 'svg' ? '⏳ Exporting…' : '⬇ SVG (vector)'}
            </button>
            {text && <button className="atp-preset-btn" style={{ color: '#ef4444', borderColor: '#fecaca' }} onClick={() => setText('')}>Clear</button>}
          </div>
        </div>

        {/* Preview box */}
        <div className="atp-preview-box" ref={containerRef} style={{ background: bgColor }}>
          {text
            ? (
              <span
                ref={textRef}
                className={`atp-font-${fontId}`}
                style={{
                  fontSize: fontSizeStr,
                  color,
                  direction: 'rtl',
                  lineHeight: 1.4,
                  whiteSpace: fitWidth ? 'nowrap' : 'pre-wrap',
                  wordBreak: fitWidth ? 'normal' : 'break-word',
                  textAlign: 'center',
                }}
              >
                {text}
              </span>
            )
            : <span className="atp-preview-placeholder">النص سيظهر هنا</span>
          }
        </div>

        <p className="atp-info">
          font: {activeFont.label} &nbsp;|&nbsp; size: {fontSizeStr}
          {fitWidth && ' | fit-width: on'}
        </p>

      </div>
    </div>
  );
}
